/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import {
  EventActionResourceReportsFilterByFields,
  EventActionResourceReportsFilterByInput,
  EventsFilterByFields,
  EventsFilterByInput,
  FilterComparisonOperator,
  PastActionsForResourceFilterByFields,
  PastActionsForResourceFilterByInput,
  ResourceExecutionErrorCode,
} from "~/generated/graphql";
import {
  LOG_ERROR_CODE_FILTER_VALUES,
  LOG_RELATED_ERROR_CODES,
} from "~/constants";

type LogErrorCodeFilterField =
  | EventsFilterByFields.ErrorCode
  | PastActionsForResourceFilterByFields.ErrorCode
  | EventActionResourceReportsFilterByFields.ErrorCode;

type LogErrorCodeFilterInput =
  | EventsFilterByInput
  | PastActionsForResourceFilterByInput
  | EventActionResourceReportsFilterByInput;

const buildErrorCodeFilter = (
  field: LogErrorCodeFilterField,
  operator: FilterComparisonOperator,
  values: ResourceExecutionErrorCode[]
): LogErrorCodeFilterInput => ({
  field,
  operator,
  values,
});

const eqErrorCodes = (
  field: LogErrorCodeFilterField,
  values: ResourceExecutionErrorCode[]
): LogErrorCodeFilterInput[] => [
  buildErrorCodeFilter(field, FilterComparisonOperator.Eq, values),
];

const neErrorCodes = (
  field: LogErrorCodeFilterField,
  values: ResourceExecutionErrorCode[]
): LogErrorCodeFilterInput[] => [
  buildErrorCodeFilter(field, FilterComparisonOperator.Ne, values),
];

const getIncludedLogErrorCodeFilters = ({
  field,
  hasAvailable,
  hasUnavailable,
  hasMalformed,
}: {
  field: LogErrorCodeFilterField;
  hasAvailable: boolean;
  hasUnavailable: boolean;
  hasMalformed: boolean;
}): LogErrorCodeFilterInput[] => {
  if (hasAvailable && hasUnavailable && hasMalformed) {
    return [];
  }

  if (hasAvailable && hasUnavailable) {
    return neErrorCodes(field, [ResourceExecutionErrorCode.LogsMalformed]);
  }

  if (hasAvailable && hasMalformed) {
    return neErrorCodes(field, [ResourceExecutionErrorCode.LogsNotAvailable]);
  }

  if (hasAvailable) {
    return neErrorCodes(field, [...LOG_RELATED_ERROR_CODES]);
  }

  if (hasUnavailable && hasMalformed) {
    return eqErrorCodes(field, [...LOG_RELATED_ERROR_CODES]);
  }

  if (hasUnavailable) {
    return eqErrorCodes(field, [ResourceExecutionErrorCode.LogsNotAvailable]);
  }

  if (hasMalformed) {
    return eqErrorCodes(field, [ResourceExecutionErrorCode.LogsMalformed]);
  }

  return [];
};

const getExcludedLogErrorCodeFilters = ({
  field,
  hasAvailable,
  hasUnavailable,
  hasMalformed,
}: {
  field: LogErrorCodeFilterField;
  hasAvailable: boolean;
  hasUnavailable: boolean;
  hasMalformed: boolean;
}): LogErrorCodeFilterInput[] => {
  if (hasAvailable && hasUnavailable && hasMalformed) {
    return [
      ...eqErrorCodes(field, [...LOG_RELATED_ERROR_CODES]),
      ...neErrorCodes(field, [...LOG_RELATED_ERROR_CODES]),
    ];
  }

  if (hasAvailable && hasUnavailable) {
    return [
      ...eqErrorCodes(field, [ResourceExecutionErrorCode.LogsMalformed]),
      ...neErrorCodes(field, [ResourceExecutionErrorCode.LogsNotAvailable]),
    ];
  }

  if (hasAvailable && hasMalformed) {
    return [
      ...eqErrorCodes(field, [ResourceExecutionErrorCode.LogsNotAvailable]),
      ...neErrorCodes(field, [ResourceExecutionErrorCode.LogsMalformed]),
    ];
  }

  if (hasAvailable) {
    return eqErrorCodes(field, [...LOG_RELATED_ERROR_CODES]);
  }

  if (hasUnavailable && hasMalformed) {
    return neErrorCodes(field, [...LOG_RELATED_ERROR_CODES]);
  }

  if (hasUnavailable) {
    return neErrorCodes(field, [ResourceExecutionErrorCode.LogsNotAvailable]);
  }

  if (hasMalformed) {
    return neErrorCodes(field, [ResourceExecutionErrorCode.LogsMalformed]);
  }

  return [];
};

export function prepareLogErrorCodeFilterBy(
  field: EventsFilterByFields.ErrorCode,
  selectedValues: string[] | undefined,
  exclude: boolean | undefined
): EventsFilterByInput[];
export function prepareLogErrorCodeFilterBy(
  field: PastActionsForResourceFilterByFields.ErrorCode,
  selectedValues: string[] | undefined,
  exclude: boolean | undefined
): PastActionsForResourceFilterByInput[];
export function prepareLogErrorCodeFilterBy(
  field: EventActionResourceReportsFilterByFields.ErrorCode,
  selectedValues: string[] | undefined,
  exclude: boolean | undefined
): EventActionResourceReportsFilterByInput[];
export function prepareLogErrorCodeFilterBy(
  field: LogErrorCodeFilterField,
  selectedValues: string[] | undefined,
  exclude: boolean | undefined
): LogErrorCodeFilterInput[] {
  if (!selectedValues?.length) {
    return [];
  }

  const hasAvailable = selectedValues.includes(
    LOG_ERROR_CODE_FILTER_VALUES.AVAILABLE
  );
  const hasUnavailable = selectedValues.includes(
    LOG_ERROR_CODE_FILTER_VALUES.UNAVAILABLE
  );
  const hasMalformed = selectedValues.includes(
    LOG_ERROR_CODE_FILTER_VALUES.MALFORMED
  );

  return exclude
    ? getExcludedLogErrorCodeFilters({
        field,
        hasAvailable,
        hasUnavailable,
        hasMalformed,
      })
    : getIncludedLogErrorCodeFilters({
        field,
        hasAvailable,
        hasUnavailable,
        hasMalformed,
      });
}
